import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Eye, EyeOff } from 'lucide-react';
import api from '@/lib/api';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function googleLogin() {
  const redirectUrl = window.location.origin + '/auth/callback';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Signup() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [refCode, setRefCode] = useState(searchParams.get('ref')?.toUpperCase() || '');
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      // Direct API call so we can pass referral_code (register() in useAuth doesn't)
      const coinParam = searchParams.get('coin');
      const res = await api.post('/auth/register', { name, email, password, referral_code: refCode || null, coin: coinParam || null });
      localStorage.setItem('cx_token', res.data.token);
      const bonus = refCode ? ' (+100 USDT referral bonus)' : '';
      const coinExtra = coinParam ? ` · trade ${coinParam.toUpperCase()} first for +25 USDT bonus` : '';
      toast.success(`Welcome — $10,000 USDT credited${bonus}${coinExtra}`);
      window.location.href = '/dashboard';
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Signup failed');
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0E11] grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-12 bg-[#181A20] border-r border-[#2B3139]">
        <Link to="/" className="flex items-center gap-2">
          <img src="/bitgonode-logo.png" alt="BITGONODE" className="h-20 w-auto object-contain" />
        </Link>
        <div>
          <div className="font-display text-white text-4xl font-bold tracking-tighter mb-4">
            Get $10,000 in demo <span className="text-[#FCD535]">USDT</span> instantly.
          </div>
          <p className="text-[#B7BDC6] max-w-md leading-relaxed">
            No KYC needed to start. Trade, stake, and explore the platform. Verify later to unlock withdrawals.
          </p>
        </div>
        <div className="text-xs text-[#5E6673]">© 2026 BITGONODE — Demo Exchange</div>
      </div>
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <h1 className="font-display text-white text-3xl font-bold tracking-tight mb-2">Create account</h1>
          <p className="text-[#848E9C] text-sm mb-8">Sign up in seconds. KYC is optional and can be completed later.</p>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label htmlFor="name" className="text-[#B7BDC6] text-xs uppercase tracking-wider">Name</Label>
              <Input id="name" value={name} onChange={e=>setName(e.target.value)} required
                className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-11 text-white focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535] mt-1.5"
                data-testid="signup-name-input" />
            </div>
            <div>
              <Label htmlFor="email" className="text-[#B7BDC6] text-xs uppercase tracking-wider">Email</Label>
              <Input id="email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required
                className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-11 text-white focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535] mt-1.5"
                data-testid="signup-email-input" />
            </div>
            <div>
              <Label htmlFor="password" className="text-[#B7BDC6] text-xs uppercase tracking-wider">Password (min 6)</Label>
              <div className="relative mt-1.5">
                <Input id="password" type={showPw ? 'text' : 'password'} value={password} onChange={e=>setPassword(e.target.value)} required
                  className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-11 text-white focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535] pr-10"
                  data-testid="signup-password-input" />
                <button type="button" onClick={()=>setShowPw(v=>!v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#848E9C] hover:text-white"
                  data-testid="signup-toggle-show-pw" aria-label={showPw ? 'Hide password' : 'Show password'}>
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="ref" className="text-[#B7BDC6] text-xs uppercase tracking-wider">Referral Code (Optional — get +100 USDT)</Label>
              <Input id="ref" value={refCode} onChange={e=>setRefCode(e.target.value.toUpperCase())} placeholder="e.g. 7FB9BBDC"
                className="bg-[#0B0E11] border-[#2B3139] rounded-sm h-11 text-white font-mono uppercase tracking-widest focus-visible:ring-[#FCD535]/40 focus-visible:border-[#FCD535] mt-1.5"
                data-testid="signup-ref-input" />
            </div>
            <Button type="submit" disabled={loading}
              className="w-full bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] font-semibold h-11 rounded-sm"
              data-testid="signup-submit-btn">
              {loading ? 'Creating account...' : 'Create account'}
            </Button>
          </form>
          <div className="flex items-center gap-3 my-6">
            <div className="flex-1 h-px bg-[#2B3139]" />
            <div className="text-xs text-[#5E6673]">OR</div>
            <div className="flex-1 h-px bg-[#2B3139]" />
          </div>
          <Button onClick={googleLogin} variant="outline"
            className="w-full border-[#2B3139] bg-[#181A20] hover:bg-[#2B3139] text-white h-11 rounded-sm"
            data-testid="signup-google-btn">
            <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.7 4.7-6.2 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.4 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34 6.1 29.3 4 24 4c-7.7 0-14.4 4.4-17.7 10.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2c-2 1.5-4.5 2.4-7.2 2.4-5.1 0-9.5-3.3-11.2-7.9L6.3 33.1C9.5 39.4 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.4 4.3-4.4 5.6l6.2 5.2c-.4.4 6.9-5 6.9-14.8 0-1.3-.1-2.3-.4-3.5z"/></svg>
            Continue with Google
          </Button>
          <div className="text-sm text-[#848E9C] mt-6 text-center">
            Already have an account? <Link to="/login" className="text-[#FCD535] hover:underline" data-testid="link-login">Log in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
